/**
 * Gezondheid — de klantenservice in cijfers (ontwerp v4 §8, plak 2.7).
 *
 * Drie kolommen, zoals de mock: links de klantenservice met de
 * staafgrafiek en de tevredenheid, in het midden checkout, ankers en
 * ordercontrole, rechts de AI-kosten, de wekkers en de storingen.
 *
 * Elk blok haalt zijn eigen gegevens op. Dat is met opzet: één trage of
 * stukke bron mag de andere blokken niet meenemen, en een blok dat niet
 * geladen kan worden zegt dat zelf in plaats van leeg te blijven.
 *
 * De kerncijfers gaan over de laatste **afgeronde** periode; de lopende
 * periode staat in de grafiek met "tot nu toe" erbij. Een halve maand
 * naast een volle maand leggen geeft altijd een daling die er niet is.
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { werkplekGezondheidApi } from "@/lib/werkplek-api";

import { BlokkenKolom, FeitenLijst } from "./FeitenLijst";
import { KanaalGrafiek } from "./KanaalGrafiek";
import { KoppelingenBlok } from "./Koppelingen";
import { MailconceptKosten } from "./MailconceptKosten";
import { PerVraagtype } from "./PerVraagtype";
import { Schaderegister } from "./Schaderegister";
import { UitlegTip } from "./UitlegTip";
import { V8Regel } from "./V8Regel";
import "./gezondheid.css";
import { MetCopiloot } from "../Copiloot/MetCopiloot";

/**
 * De eerste zin van een uitleg, voor de regel naast het i-icoon.
 *
 * Geen harde afkap op tekens: dat knipt midden in een woord. Eén zin is
 * altijd een afgeronde gedachte, en de rest staat in de tooltip.
 */
function eersteZin(tekst: string): string {
  const schoon = (tekst ?? "").trim();
  const punt = schoon.search(/[.!?](\s|$)/);
  return punt === -1 ? schoon : schoon.slice(0, punt + 1);
}

const PERIODES: { sleutel: string; label: string }[] = [
  { sleutel: "week", label: "Week" },
  { sleutel: "maand", label: "Maand" },
  { sleutel: "jaar", label: "Jaar" },
];

const TABS = ["Klantenservice", "Checkout", "AI en cases"];

function verschilKlasse(richting: string | null): string {
  if (richting === "op") return "wp-gz-verschil-op";
  if (richting === "neer") return "wp-gz-verschil-neer";
  return "wp-gz-verschil-gelijk";
}

export function WerkplekGezondheid() {
  const [periode, setPeriode] = useState("week");
  const [tab, setTab] = useState(0);

  const klantenservice = useQuery({
    queryKey: ["werkplek", "gezondheid", "klantenservice", periode],
    queryFn: () => werkplekGezondheidApi.klantenservice(periode),
  });
  const tevredenheid = useQuery({
    queryKey: ["werkplek", "gezondheid", "tevredenheid"],
    queryFn: werkplekGezondheidApi.tevredenheid,
  });
  const checkout = useQuery({
    queryKey: ["werkplek", "gezondheid", "checkout"],
    queryFn: werkplekGezondheidApi.checkout,
  });
  const systeem = useQuery({
    queryKey: ["werkplek", "gezondheid", "systeem"],
    queryFn: werkplekGezondheidApi.systeem,
  });
  // Sessie 47 (2c): afgerond zonder antwoord per week, eigen verzoek.
  const afronden = useQuery({
    queryKey: ["werkplek", "gezondheid", "afronden"],
    queryFn: werkplekGezondheidApi.afronden,
  });

  const cijfers = klantenservice.data;

  return (
    <>
      <div className="wp-bovenbalk">
        <h1 style={{ fontSize: 17, margin: 0, fontWeight: 600 }}>Gezondheid</h1>
        <span style={{ fontSize: 12, color: "var(--wp-tekst3)" }}>
          Klantenservice, checkout, ankers, ordercontrole, cases en AI-kosten
        </span>
      </div>

      <div className="wp-inhoud">
        <MetCopiloot pagina="gezondheid">
        {/* Sessie 125: de koppelingscontrole per bron bovenaan, alleen
            voor de beheerder, eigen verzoek. Het motorrapport van motor
            6.1 erboven is in sessie 8D weggehaald. */}
        <KoppelingenBlok />
        <div className="wp-mobtabs" role="tablist" aria-label="Gezondheid">
          {TABS.map((naam, index) => (
            <button
              key={naam}
              type="button"
              role="tab"
              aria-selected={tab === index}
              className="wp-knop"
              onClick={() => setTab(index)}
            >
              {naam}
            </button>
          ))}
        </div>

        <div className="wp-gz-kolommen">
          <div className="wp-gz-kolom" data-mob-aan={tab === 0 ? "ja" : "nee"}>
            <div className="wp-blok">
              <h4 style={{ display: "flex", alignItems: "center" }}>
                Klantenservice per{" "}
                {PERIODES.find((p) => p.sleutel === periode)?.label.toLowerCase()}
                <span className="wp-gz-periodes">
                  {PERIODES.map((keuze) => (
                    <button
                      key={keuze.sleutel}
                      type="button"
                      aria-pressed={periode === keuze.sleutel}
                      onClick={() => setPeriode(keuze.sleutel)}
                    >
                      {keuze.label}
                    </button>
                  ))}
                </span>
              </h4>

              {klantenservice.isLoading ? (
                <p className="wp-gz-uitleg">Bezig met laden…</p>
              ) : klantenservice.isError || !cijfers ? (
                <p
                  className="wp-gz-uitleg"
                  style={{ color: "var(--wp-rood)" }}
                >
                  De klantenservice-cijfers zijn niet op te halen. Alleen een
                  beheerder ziet dit scherm.
                </p>
              ) : (
                <>
                  <KanaalGrafiek
                    staven={cijfers.staven}
                    kanalen={cijfers.kanalen}
                    maximum={cijfers.maximum}
                    norm={cijfers.norm}
                    normLabel={cijfers.norm_label}
                    redenLeeg={cijfers.reden_leeg}
                  />
                  {cijfers.kerncijfers.length > 0 ? (
                    <div className="wp-gz-kerncijfers">
                      {cijfers.kerncijfers.map((cijfer) => (
                        <div className="wp-gz-kerncijfer" key={cijfer.sleutel}>
                          {/* Een uitkomst die geen getal is, krijgt
                              een kleinere letter: anders leest een zin
                              als een meting (JP, punt 39). */}
                          <b
                            data-tekst={
                              /\d/.test(cijfer.waarde) ? "nee" : "ja"
                            }
                          >
                            {cijfer.waarde}
                          </b>
                          <span>
                            {cijfer.label}
                            {cijfers.kerncijfer_label
                              ? `, ${cijfers.kerncijfer_label}`
                              : ""}
                          </span>
                          {/* Wat het cijfer meet (JP, punt 41 van
                              22-09). Een mediaan zonder definitie is
                              een getal waar je niets mee kunt. Maar
                              voluit in de tegel duwde de uitleg het
                              cijfer weg (punt 11 van sessie 43), dus:
                              één regel plus een i met de hele tekst. */}
                          {cijfer.uitleg ? (
                            <UitlegTip
                              tekst={cijfer.uitleg}
                              kort={eersteZin(cijfer.uitleg)}
                              label={`Wat betekent ${cijfer.label}?`}
                            />
                          ) : null}
                          <span className={verschilKlasse(cijfer.richting)}>
                            {cijfer.verschil
                              ? `${cijfer.verschil}${
                                  cijfers.vorige_label
                                    ? ` op ${cijfers.vorige_label}`
                                    : ""
                                }`
                              : "geen vorige periode om mee te vergelijken"}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </>
              )}
            </div>

            {tevredenheid.isLoading ? (
              <p className="wp-gz-uitleg">Bezig met laden…</p>
            ) : tevredenheid.isError || !tevredenheid.data ? (
              <p className="wp-gz-uitleg" style={{ color: "var(--wp-rood)" }}>
                De tevredenheidscijfers zijn niet op te halen.
              </p>
            ) : (
              <FeitenLijst
                blok={{
                  titel: "Tevredenheid na afronding",
                  ondertitel: `${tevredenheid.data.periode_label} · 1 klik in de mail`,
                  reden_leeg: tevredenheid.data.reden_leeg,
                  feiten: [
                    {
                      term: "Antwoorden",
                      waarde: `${tevredenheid.data.antwoorden} van ${tevredenheid.data.verstuurd} verstuurde mails${
                        tevredenheid.data.aandeel_procent !== null
                          ? ` (${tevredenheid.data.aandeel_procent}%)`
                          : ""
                      }`,
                      stand: null,
                      chip: null,
                    },
                    ...tevredenheid.data.per_schrijver.map((rij) => ({
                      term: rij.label,
                      waarde: `${rij.gemiddelde?.toFixed(1) ?? "geen"} van 5 · ${rij.aantal} antwoorden`,
                      stand: null,
                      chip: null,
                    })),
                    {
                      term: "Onder de 3",
                      // Zonder antwoorden valt er niets te tellen: "geen"
                      // leest dan als een meting van nul (JP, punt 39).
                      waarde:
                        tevredenheid.data.antwoorden === 0
                          ? "nog geen antwoorden"
                          : tevredenheid.data.onder_drie === 0
                          ? "geen keer"
                          : `${tevredenheid.data.onder_drie} keer${
                              tevredenheid.data.onder_drie_categorieen.length
                                ? ` · ${tevredenheid.data.onder_drie_categorieen.join(", ")}`
                                : ""
                            }`,
                      stand:
                        tevredenheid.data.antwoorden === 0
                          ? null
                          : tevredenheid.data.onder_drie
                            ? "let_op"
                            : "goed",
                      chip:
                        tevredenheid.data.antwoorden === 0 ||
                        tevredenheid.data.onder_drie
                          ? null
                          : "in de pas",
                    },
                  ],
                }}
              />
            )}
            {tevredenheid.data?.uitleg ? (
              <p className="wp-gz-uitleg">
                <UitlegTip
                  tekst={tevredenheid.data.uitleg}
                  kort={eersteZin(tevredenheid.data.uitleg)}
                  label="Wat betekent dit cijfer?"
                />
              </p>
            ) : null}
          </div>

          <div className="wp-gz-kolom" data-mob-aan={tab === 1 ? "ja" : "nee"}>
            <BlokkenKolom
              blokken={checkout.data?.blokken}
              isLoading={checkout.isLoading}
              isError={checkout.isError}
              watFoutGing="De checkout-cijfers zijn niet op te halen."
            />
          </div>

          <div className="wp-gz-kolom" data-mob-aan={tab === 2 ? "ja" : "nee"}>
            {/* Motor sessie 8H: V8 boven de AI-kosten, eigen verzoek. */}
            <V8Regel />
            <BlokkenKolom
              blokken={systeem.data?.blokken}
              isLoading={systeem.isLoading}
              isError={systeem.isError}
              watFoutGing="De AI-kosten en wekkers zijn niet op te halen."
            />
            <BlokkenKolom
              blokken={afronden.data?.blokken}
              isLoading={afronden.isLoading}
              isError={afronden.isError}
              watFoutGing="De meting van afronden zonder antwoord is niet op te halen."
            />
          </div>
        </div>

        {/* Sessie 53: eigen verzoek, zodat een trage query de rest laat staan.
            Blok 1 (G2): een eigen rij over de volle breedte onder de
            kolommen, ook op de telefoon onder elk tabblad. */}
        <section className="wp-gz-breed" aria-label="Kosten per mailconcept">
          <MailconceptKosten />
        </section>
        {/* Sessie 53 blok 3 (L3): stand, correcties en kosten per
            vraagtype, eigen verzoek en eigen brede rij. */}
        <section className="wp-gz-breed" aria-label="Per vraagtype">
          <PerVraagtype />
        </section>
        {/* Sessie 55 blok 2: schade per vervoerder, kwaliteit per merk en
            herhalers; eigen verzoek en eigen brede rij. */}
        <section className="wp-gz-breed" aria-label="Schaderegister">
          <Schaderegister />
        </section>
      </MetCopiloot>
      </div>
    </>
  );
}
